import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Plus, Trash, Buildings, DownloadSimple, UploadSimple } from "@phosphor-icons/react";
import { toast } from "sonner";

const EMPTY_BRAND = {
  id: "", name: "", short_name: "", address: "", phone: "", email: "",
  bank_details: "", logo_text: "", invoice_prefix: "INV", quotation_prefix: "QT",
  accent_color: "#0020C2", signature_data_url: "", stamp_data_url: "",
};

const ImageUploadField = ({ label, value, onChange, testId, emptyLabel }) => (
  <Field label={label}>
    <div className="flex items-center gap-3">
      {value ? (
        <img src={value} alt="" className="h-12 max-w-[140px] object-contain bg-zinc-50 border border-zinc-200 p-1" />
      ) : (
        <div className="h-12 w-24 grid place-items-center text-[10px] text-zinc-400 border border-dashed border-zinc-300">{emptyLabel}</div>
      )}
      <div className="flex flex-col gap-1">
        <input
          type="file"
          accept="image/png,image/jpeg,image/jpg,image/webp"
          data-testid={testId}
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (!f) return;
            if (f.size > 2_000_000) { alert(`${label} must be under 2MB`); return; }
            const reader = new FileReader();
            reader.onload = () => onChange(reader.result);
            reader.readAsDataURL(f);
          }}
          className="text-xs"
        />
        {value && (
          <button onClick={() => onChange("")} className="text-[11px] text-red-700 underline text-left">Remove</button>
        )}
      </div>
    </div>
  </Field>
);

export default function Settings() {
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/settings").then((r) => setData(r.data)); }, []);

  if (!data) return <div className="label-mini">Loading…</div>;

  const brands = data.brands || [];

  const updateBrand = (idx, patch) => {
    const next = JSON.parse(JSON.stringify(data));
    next.brands[idx] = { ...next.brands[idx], ...patch };
    setData(next);
  };
  const addBrand = () => {
    const next = JSON.parse(JSON.stringify(data));
    const newId = `brand_${(next.brands?.length || 0) + 1}`;
    next.brands = [...(next.brands || []), { ...EMPTY_BRAND, id: newId, name: "New Brand", logo_text: "NEW BRAND" }];
    setData(next);
  };
  const removeBrand = (idx) => {
    if (!window.confirm("Remove this brand? Orders already using it will fall back to the first remaining brand.")) return;
    const next = JSON.parse(JSON.stringify(data));
    next.brands.splice(idx, 1);
    setData(next);
  };

  const updateRate = (k, v) => {
    const next = JSON.parse(JSON.stringify(data));
    next.rates[k] = Number(v);
    setData(next);
  };
  const updateField = (k, v) => {
    const next = JSON.parse(JSON.stringify(data));
    next[k] = v;
    setData(next);
  };
  const updateAI = (k, v) => {
    const next = JSON.parse(JSON.stringify(data));
    next.ai = { ...(next.ai || {}), [k]: v };
    setData(next);
  };

  const save = async () => {
    setSaving(true);
    try {
      const { id, ...payload } = data;
      // Strip the read-only flag and don't send an empty key (server keeps the saved one).
      if (payload.ai) {
        const { api_key_set, ...ai } = payload.ai;
        if (!ai.api_key) delete ai.api_key;
        payload.ai = ai;
      }
      await api.put("/settings", payload);
      toast.success("Settings saved");
      const { data: fresh } = await api.get("/settings");
      setData(fresh);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-5" data-testid="settings-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="label-mini">System Configuration</div>
          <h1 className="text-3xl font-bold tracking-tight mt-1">Settings</h1>
        </div>
        <Button onClick={save} disabled={saving} className="rounded-sm bg-black text-white hover:bg-zinc-800" data-testid="save-settings-btn">
          {saving ? "Saving…" : "Save Changes"}
        </Button>
      </div>

      <Tabs defaultValue="brands">
        <TabsList className="rounded-sm bg-zinc-100 border border-zinc-200">
          <TabsTrigger value="brands" className="rounded-sm" data-testid="tab-brands">Brands</TabsTrigger>
          <TabsTrigger value="rates" className="rounded-sm" data-testid="tab-rates">Exchange Rates</TabsTrigger>
          <TabsTrigger value="terms" className="rounded-sm" data-testid="tab-terms">Invoice Terms</TabsTrigger>
          <TabsTrigger value="ai" className="rounded-sm" data-testid="tab-ai">AI Assistant</TabsTrigger>
          <TabsTrigger value="items-import" className="rounded-sm" data-testid="tab-items-import">Bulk Item Import</TabsTrigger>
        </TabsList>

        <TabsContent value="brands" className="mt-4 space-y-4">
          <div className="text-xs text-zinc-500">
            Each brand has its own logo text, contact info, bank details and document number series.
            When creating an order you choose which brand to issue the quotation/invoice under.
          </div>
          <div className="grid lg:grid-cols-2 gap-4">
            {brands.map((b, idx) => (
              <div key={b.id || idx} className="card-sharp p-5 relative" data-testid={`brand-card-${b.id}`}>
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Buildings size={16} />
                    <h3 className="text-lg font-semibold tracking-tight">{b.name || "Untitled"}</h3>
                  </div>
                  <button onClick={() => removeBrand(idx)} className="text-zinc-400 hover:text-red-600 p-1" data-testid={`remove-brand-${b.id}`}>
                    <Trash size={14} />
                  </button>
                </div>
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <Field label="ID (used in URLs)">
                      <Input value={b.id} onChange={(e) => updateBrand(idx, { id: e.target.value.replace(/[^a-z0-9_]/g, "_") })} className="rounded-sm h-9 font-mono" />
                    </Field>
                    <Field label="Short Name (badge)">
                      <Input value={b.short_name || ""} onChange={(e) => updateBrand(idx, { short_name: e.target.value })} className="rounded-sm h-9" />
                    </Field>
                  </div>
                  <Field label="Display Name">
                    <Input value={b.name} onChange={(e) => updateBrand(idx, { name: e.target.value })} className="rounded-sm h-9" data-testid={`brand-name-${b.id}`} />
                  </Field>
                  <Field label="Logo Text (used when no logo image)">
                    <Input value={b.logo_text || ""} onChange={(e) => updateBrand(idx, { logo_text: e.target.value })} className="rounded-sm h-9 font-mono" />
                  </Field>
                  <Field label="Logo Image (PNG/JPG, used on PDF)">
                    <div className="flex items-center gap-3">
                      {b.logo_data_url ? (
                        <img src={b.logo_data_url} alt="" className="h-12 max-w-[140px] object-contain bg-zinc-50 border border-zinc-200 p-1" />
                      ) : (
                        <div className="h-12 w-24 grid place-items-center text-[10px] text-zinc-400 border border-dashed border-zinc-300">No logo</div>
                      )}
                      <div className="flex flex-col gap-1">
                        <input
                          type="file"
                          accept="image/png,image/jpeg,image/jpg,image/webp"
                          data-testid={`brand-logo-${b.id}`}
                          onChange={async (e) => {
                            const f = e.target.files?.[0];
                            if (!f) return;
                            if (f.size > 2_000_000) { alert("Logo must be under 2MB"); return; }
                            const reader = new FileReader();
                            reader.onload = () => updateBrand(idx, { logo_data_url: reader.result });
                            reader.readAsDataURL(f);
                          }}
                          className="text-xs"
                        />
                        {b.logo_data_url && (
                          <button onClick={() => updateBrand(idx, { logo_data_url: "" })} className="text-[11px] text-red-700 underline text-left">Remove logo</button>
                        )}
                      </div>
                    </div>
                  </Field>
                  <Field label="Address">
                    <textarea value={b.address || ""} onChange={(e) => updateBrand(idx, { address: e.target.value })} className="w-full border border-zinc-200 rounded-sm p-2 text-sm h-16" />
                  </Field>
                  <div className="grid grid-cols-2 gap-3">
                    <Field label="Phone">
                      <Input value={b.phone || ""} onChange={(e) => updateBrand(idx, { phone: e.target.value })} className="rounded-sm h-9" />
                    </Field>
                    <Field label="Email">
                      <Input value={b.email || ""} onChange={(e) => updateBrand(idx, { email: e.target.value })} className="rounded-sm h-9" />
                    </Field>
                  </div>
                  <Field label="NTN / Tax ID (printed on invoice)">
                    <Input value={b.ntn || ""} onChange={(e) => updateBrand(idx, { ntn: e.target.value })} className="rounded-sm h-9 font-mono" data-testid={`brand-ntn-${b.id}`} placeholder="e.g. 1234567-8" />
                  </Field>
                  <div className="grid grid-cols-2 gap-3">
                    <Field label="Quotation Prefix">
                      <Input value={b.quotation_prefix || ""} onChange={(e) => updateBrand(idx, { quotation_prefix: e.target.value })} className="rounded-sm h-9 font-mono" />
                    </Field>
                    <Field label="Invoice Prefix">
                      <Input value={b.invoice_prefix || ""} onChange={(e) => updateBrand(idx, { invoice_prefix: e.target.value })} className="rounded-sm h-9 font-mono" />
                    </Field>
                  </div>
                  <Field label="Bank Details (printed on invoice)">
                    <textarea value={b.bank_details || ""} onChange={(e) => updateBrand(idx, { bank_details: e.target.value })} className="w-full border border-zinc-200 rounded-sm p-2 text-sm h-24 font-mono" />
                  </Field>
                  <ImageUploadField
                    label="Signature Image (printed on invoice)"
                    value={b.signature_data_url}
                    onChange={(v) => updateBrand(idx, { signature_data_url: v })}
                    testId={`brand-signature-${b.id}`}
                    emptyLabel="No signature"
                  />
                  <ImageUploadField
                    label="Stamp Image (printed on invoice)"
                    value={b.stamp_data_url}
                    onChange={(v) => updateBrand(idx, { stamp_data_url: v })}
                    testId={`brand-stamp-${b.id}`}
                    emptyLabel="No stamp"
                  />
                </div>
              </div>
            ))}
          </div>
          <Button onClick={addBrand} variant="outline" className="rounded-sm border-black" data-testid="add-brand-btn">
            <Plus size={14} className="mr-1" /> Add Brand
          </Button>
        </TabsContent>

        <TabsContent value="rates" className="mt-4">
          <div className="card-sharp p-5 max-w-lg">
            <h3 className="text-lg font-semibold tracking-tight mb-3">Exchange Rates (to PKR)</h3>
            <div className="space-y-3">
              <Field label="1 USD = ? PKR">
                <Input type="number" value={data.rates.USD} onChange={(e) => updateRate("USD", e.target.value)} className="rounded-sm h-9 font-mono" data-testid="rate-usd" />
              </Field>
              <Field label="1 CNY = ? PKR">
                <Input type="number" value={data.rates.CNY} onChange={(e) => updateRate("CNY", e.target.value)} className="rounded-sm h-9 font-mono" data-testid="rate-cny" />
              </Field>
              <div className="text-[11px] text-zinc-500">
                Rates are applied at the moment of order creation / payment recording and stored historically — past records will not change when you update these.
              </div>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="terms" className="mt-4">
          <div className="card-sharp p-5 max-w-2xl mb-4">
            <h3 className="text-lg font-semibold tracking-tight mb-3">Default GST Rate</h3>
            <Field label="GST % applied to new quotations">
              <Input
                type="number" min="0" max="100" step="0.01"
                value={data.default_gst_percent ?? 0}
                onChange={(e) => updateField("default_gst_percent", Number(e.target.value))}
                className="rounded-sm h-9 font-mono max-w-[12rem]"
                data-testid="default-gst-percent"
              />
            </Field>
            <div className="text-[11px] text-zinc-500 mt-2">
              Pre-fills the GST field on new quotations; it stays editable per order, and 0 means no GST. Existing orders keep the rate they were quoted at.
            </div>
          </div>
          <div className="card-sharp p-5 max-w-2xl">
            <h3 className="text-lg font-semibold tracking-tight mb-3">Default Invoice Terms</h3>
            <Field label="Terms &amp; Conditions">
              <textarea value={data.invoice_terms} onChange={(e) => updateField("invoice_terms", e.target.value)} className="w-full border border-zinc-200 rounded-sm p-2 text-sm h-40" />
            </Field>
            <div className="text-[11px] text-zinc-500 mt-2">
              Printed on both quotations and invoices for every brand.
            </div>
          </div>
        </TabsContent>

        <TabsContent value="ai" className="mt-4">
          <div className="card-sharp p-5 max-w-2xl space-y-4" data-testid="ai-settings-panel">
            <div>
              <h3 className="text-lg font-semibold tracking-tight">AI Assistant Provider</h3>
              <p className="text-xs text-zinc-500 mt-1">
                Connect any OpenAI-compatible API to power the AI Analyst. Works with OpenAI,
                OpenRouter, Groq, Together, Anthropic (compat), Gemini (compat) and local
                servers like Ollama or LM Studio.
              </p>
            </div>
            <Field label="Provider Base URL">
              <Input
                data-testid="ai-base-url"
                value={data.ai?.base_url || ""}
                onChange={(e) => updateAI("base_url", e.target.value)}
                placeholder="https://api.openai.com/v1"
                className="rounded-sm h-9 font-mono"
              />
            </Field>
            <Field label="Model">
              <Input
                data-testid="ai-model"
                value={data.ai?.model || ""}
                onChange={(e) => updateAI("model", e.target.value)}
                placeholder="gpt-4o-mini"
                className="rounded-sm h-9 font-mono"
              />
            </Field>
            <Field label="API Key">
              <Input
                data-testid="ai-api-key"
                type="password"
                value={data.ai?.api_key || ""}
                onChange={(e) => updateAI("api_key", e.target.value)}
                placeholder={data.ai?.api_key_set ? "•••••••• (saved — leave blank to keep)" : "Enter your API key"}
                className="rounded-sm h-9 font-mono"
              />
              <div className="text-[11px] text-zinc-500 mt-1">
                {data.ai?.api_key_set
                  ? "A key is already saved. Enter a new one to replace it, or leave blank to keep the current key."
                  : "The key is stored securely on the server and never sent back to the browser."}
              </div>
            </Field>
            <div className="text-[11px] text-zinc-500 border-t border-zinc-100 pt-3">
              <span className="font-semibold">Common examples:</span>
              <div className="mt-1 space-y-0.5 font-mono">
                <div>OpenAI · https://api.openai.com/v1 · gpt-4o-mini</div>
                <div>OpenRouter · https://openrouter.ai/api/v1 · anthropic/claude-3.5-sonnet</div>
                <div>Groq · https://api.groq.com/openai/v1 · llama-3.3-70b-versatile</div>
                <div>Ollama (local) · http://localhost:11434/v1 · llama3.1</div>
              </div>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="items-import" className="mt-4">
          <ItemsImportPanel />
        </TabsContent>
      </Tabs>
    </div>
  );
}

const ItemsImportPanel = () => {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);

  const downloadTemplate = async () => {
    const res = await api.get("/items/import/template", { responseType: "blob" });
    const url = URL.createObjectURL(new Blob([res.data]));
    const a = document.createElement("a");
    a.href = url; a.download = "items_import_template.xlsx";
    a.click();
    URL.revokeObjectURL(url);
  };

  const upload = async () => {
    if (!file) return;
    setUploading(true);
    setResult(null);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await api.post("/items/import", formData);
      setResult(res.data);
      toast.success(`Imported ${res.data.created} item(s)`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Import failed");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="card-sharp p-5 max-w-2xl space-y-4" data-testid="items-import-panel">
      <div>
        <h3 className="text-lg font-semibold tracking-tight">Bulk Item Import</h3>
        <p className="text-xs text-zinc-500 mt-1">
          Download the template, fill in your items (replace or add below the example rows),
          then upload it to bulk-create items. Rows that exactly match an existing item are
          skipped automatically.
        </p>
      </div>
      <Button onClick={downloadTemplate} variant="outline" className="rounded-sm border-black"
              data-testid="download-item-template-btn">
        <DownloadSimple size={14} className="mr-1" /> Download Template
      </Button>
      <div className="border-t border-zinc-100 pt-4 space-y-2">
        <Field label="Upload Filled Template (.xlsx)">
          <input
            type="file"
            accept=".xlsx"
            data-testid="item-import-file"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
            className="text-xs"
          />
        </Field>
        <Button onClick={upload} disabled={!file || uploading} className="rounded-sm bg-black text-white hover:bg-zinc-800"
                data-testid="upload-item-import-btn">
          <UploadSimple size={14} className="mr-1" /> {uploading ? "Importing…" : "Upload & Import"}
        </Button>
      </div>
      {result && (
        <div className="border-t border-zinc-100 pt-4 space-y-2 text-xs" data-testid="item-import-result">
          <div className="font-semibold">
            Created {result.created} · Skipped {result.skipped_duplicate} duplicate(s)
            {result.errors.length > 0 && ` · ${result.errors.length} error(s)`}
          </div>
          {result.errors.length > 0 && (
            <div className="text-red-700 space-y-0.5">
              {result.errors.map((e, i) => <div key={i}>{e}</div>)}
            </div>
          )}
          {result.warnings.length > 0 && (
            <div className="text-amber-700 space-y-0.5">
              {result.warnings.map((w, i) => <div key={i}>{w}</div>)}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const Field = ({ label, required, children }) => (
  <div>
    <label className="label-mini">{label}{required && <span className="text-red-600">*</span>}</label>
    <div className="mt-1">{children}</div>
  </div>
);
